import React from 'react';

/**
 * EmergencyMlcSection (S4 / S1) — the MLC (medico-legal case) capture block,
 * shown ONLY when `source = emergency`. A toggle reveals 3 optional fields
 * (mlc_number, police_station, police_informed_at). All optional at capture;
 * editable later from the desk. MLC is only valid on emergency admissions
 * (server: MLC_NOT_EMERGENCY 422) — the parent hides this block on non-emergency
 * and clears MLC values on submit when not emergency.
 *
 * Controlled: `value = { is_mlc, mlc_number, police_station, police_informed_at }`,
 * `onChange(next)`.
 */
export default function EmergencyMlcSection({ value, onChange }) {
  const v = value || {};
  const setField = (field) => (e) => onChange({ ...v, [field]: e.target.value });
  const toggleMlc = (e) => onChange({ ...v, is_mlc: e.target.checked });

  const inputCls =
    'w-full px-2.5 py-2 rounded-lg border border-amber-300 dark:border-amber-700 ' +
    'bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-amber-500';
  const labelCls = 'block text-xs font-medium text-amber-700 dark:text-amber-300 mb-1';

  return (
    <div
      className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3"
      data-testid="mlc-section"
    >
      <div className="flex items-center gap-2 mb-2">
        <input
          id="mlc-toggle"
          type="checkbox"
          className="h-4 w-4 accent-amber-600"
          checked={!!v.is_mlc}
          onChange={toggleMlc}
        />
        <label htmlFor="mlc-toggle" className="text-sm font-semibold text-amber-800 dark:text-amber-200 m-0">
          Medico-legal case (MLC)
        </label>
        <span className="ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
          shows on desk + discharge summary
        </span>
      </div>

      {v.is_mlc && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mlc-fields">
          <div>
            <label className={labelCls} htmlFor="mlc-number">MLC number</label>
            <input
              id="mlc-number"
              className={inputCls}
              placeholder="MLC/2026/0113"
              value={v.mlc_number || ''}
              onChange={setField('mlc_number')}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="police-station">Police station</label>
            <input
              id="police-station"
              className={inputCls}
              placeholder="Shivaji Nagar PS"
              value={v.police_station || ''}
              onChange={setField('police_station')}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="police-informed-at">Police informed at</label>
            <input
              id="police-informed-at"
              type="datetime-local"
              className={inputCls}
              value={v.police_informed_at || ''}
              onChange={setField('police_informed_at')}
            />
          </div>
        </div>
      )}
      <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-2">
        All three optional at capture — editable later from Manage Admission.
      </p>
    </div>
  );
}
